import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import {
  DEFAULT_MAP_TOOLBAR_STATE,
  MapToolbarState,
  RenderMode,
  SwipePresetId,
} from '../../types/map.types';

export type OverlayDisplayMode = 'on-map' | 'swipe';

@Component({
  selector: 'app-map-widgets-toolbar',
  templateUrl: './map-widgets-toolbar.component.html',
  styleUrls: ['./map-widgets-toolbar.component.scss'],
  standalone: false,
})
export class MapWidgetsToolbarComponent {
  @Input() state: MapToolbarState = { ...DEFAULT_MAP_TOOLBAR_STATE };

  @Output() renderModeChange = new EventEmitter<RenderMode>();
  @Output() swipePresetChange = new EventEmitter<SwipePresetId>();
  @Output() spatialSelectToggle = new EventEmitter<boolean>();
  @Output() overlayToggle = new EventEmitter<string>();
  @Output() addOverlayClick = new EventEmitter<void>();

  expanded = false;
  overlaysOpen = false;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  /**
   * Close the popover when the user clicks anywhere outside of it — including
   * off the map, other toolbar buttons, or the Esri layers/zoom controls. The
   * Overlays button itself already toggles via its own click handler, so we
   * exclude clicks inside the whole toolbar host from triggering a close
   * (otherwise a click on the button would close-then-reopen).
   */
  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.overlaysOpen) return;
    const target = event.target as HTMLElement | null;
    if (target && this.elementRef.nativeElement.contains(target)) return;
    this.overlaysOpen = false;
  }

  toggleExpand(): void {
    this.expanded = !this.expanded;
    if (!this.expanded) this.overlaysOpen = false;
  }

  onRenderModeClick(mode: RenderMode): void {
    this.renderModeChange.emit(mode);
  }

  onHeatmapToggle(): void {
    const next: RenderMode = this.state.renderMode === 'heatmap' ? 'pins' : 'heatmap';
    this.renderModeChange.emit(next);
  }

  onSpatialSelectClick(): void {
    this.spatialSelectToggle.emit(!this.state.spatialSelectActive);
  }

  onOverlaysButtonClick(): void {
    this.overlaysOpen = !this.overlaysOpen;
  }

  onOverlayLayerToggle(id: string): void {
    this.overlayToggle.emit(id);
  }

  onAddOverlayButtonClick(event: MouseEvent): void {
    // Close the popover when the user chooses to add a new overlay — the
    // upload dialog covers the map anyway, and leaving the popover open
    // looks stale while the dialog is visible.
    event.stopPropagation();
    this.overlaysOpen = false;
    this.addOverlayClick.emit();
  }

  isOverlayOn(id: string): boolean {
    return this.state.visibleOverlayIds.includes(id);
  }

  /** Maps the current swipe preset to the toggle value. */
  get displayMode(): OverlayDisplayMode {
    if (this.state.swipePreset === 'project-design-vs-asbuilt') return 'swipe';
    return 'on-map';
  }

  // Labels shown on the rounded pill toggle. Order matters — `app-toggle-buttons`
  // emits the label string, which we map back to a mode value via MODE_BY_LABEL.
  readonly displayModeLabels = ['Overlay', 'Swipe'];
  readonly displayModeTooltips = [
    'Overlay — reference layer drawn on top of the map',
    'Swipe — drag the divider to compare design vs as-builts',
  ];
  private readonly MODE_BY_LABEL: Record<string, OverlayDisplayMode> = {
    'Overlay': 'on-map',
    'Swipe': 'swipe',
  };
  private readonly LABEL_BY_MODE: Record<OverlayDisplayMode, string> = {
    'on-map': 'Overlay',
    'swipe': 'Swipe',
  };

  get displayModeLabel(): string {
    return this.LABEL_BY_MODE[this.displayMode];
  }

  onDisplayModeLabelChange(label: string): void {
    const mode = this.MODE_BY_LABEL[label];
    if (mode) this.onDisplayModeChange(mode);
  }

  onDisplayModeChange(mode: OverlayDisplayMode): void {
    // Swipe needs at least one overlay selected to mean anything.
    if (mode === 'swipe' && !this.state.visibleOverlayIds.length) return;
    if (mode === 'swipe') {
      this.swipePresetChange.emit('project-design-vs-asbuilt');
    } else {
      this.swipePresetChange.emit('off');
    }
  }
}
